// address-autocomplete.service.ts
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
} from "rxjs/operators";

@Injectable({
  providedIn: "root",
})
export class AddressAutocompleteService implements OnDestroy {
  private searchTerms = new Subject<string>();
  private readonly GEOAPIFY_BASE_URL = "https://api.geoapify.com/v1/geocode";

  // You need to get a free API key from https://geoapify.com/
  // Sign up for free and replace this with your actual API key
  private readonly API_KEY = "34b4f91f58d9480abda1b88724d210ee";

  constructor(private http: HttpClient) {}

  ngOnDestroy(): void {
    this.searchTerms.complete();
  }

  initializeSearch(): Observable<GeoApifyAddress[]> {
    return this.searchTerms.pipe(
      debounceTime(600),
      distinctUntilChanged(),
      switchMap((term: string) => {
        return this.searchAddresses(term);
      })
    );
  }

  searchAddress(query: string): void {
    this.searchTerms.next(query);
  }

  /**
   * Search for addresses using Geoapify Autocomplete API
   * @param query Search term
   * @returns Observable of matching addresses
   */
  private searchAddresses(query: string): Observable<GeoApifyAddress[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    const params = new HttpParams()
      .set("text", query)
      .set("limit", "10")
      .set("apiKey", this.API_KEY);

    const url = `${this.GEOAPIFY_BASE_URL}/autocomplete`;

    return this.http.get<GeoapifyResponse>(url, { params }).pipe(
      map((response) => this.transformGeoapifyResponse(response)),
      catchError((error) => {
        console.error("Error fetching addresses from Geoapify:", error);
        return of([]);
      })
    );
  }

  /**
   * Search for addresses using Geoapify Search API (more detailed)
   * @param query Search term
   * @returns Observable of matching addresses
   */
  searchAddressesDetailed(query: string): Observable<GeoApifyAddress[]> {
    if (!query || query.length < 3) {
      return of([]);
    }

    const params = new HttpParams()
      .set("text", query)
      .set("limit", "10")
      .set("apiKey", this.API_KEY);

    const url = `${this.GEOAPIFY_BASE_URL}/search`;

    return this.http.get<GeoapifyResponse>(url, { params }).pipe(
      map((response) => this.transformGeoapifyResponse(response)),
      catchError((error) => {
        console.error("Error fetching addresses from Geoapify:", error);
        return of([]);
      })
    );
  }

  /**
   * Reverse geocode coordinates to get address
   * @param lat Latitude
   * @param lon Longitude
   * @returns Observable of address
   */
  reverseGeocode(lat: number, lon: number): Observable<GeoApifyAddress | null> {
    const params = new HttpParams()
      .set("lat", lat.toString())
      .set("lon", lon.toString())
      .set("apiKey", this.API_KEY);

    const url = `${this.GEOAPIFY_BASE_URL}/reverse`;

    return this.http.get<GeoapifyResponse>(url, { params }).pipe(
      map((response) => {
        const addresses = this.transformGeoapifyResponse(response);
        return addresses.length > 0 ? addresses[0] : null;
      }),
      catchError((error) => {
        console.error("Error reverse geocoding:", error);
        return of(null);
      })
    );
  }

  private transformGeoapifyResponse(
    response: GeoapifyResponse
  ): GeoApifyAddress[] {
    if (!response.features) {
      return [];
    }

    return response.features.map((feature) =>
      this.transformGeoapifyFeature(feature)
    );
  }

  private transformGeoapifyFeature(feature: GeoapifyFeature): GeoApifyAddress {
    const props = feature.properties;

    return {
      place_id: props.place_id,
      formatted: props.formatted,
      address_line1: props.address_line1,
      address_line2: props.address_line2,
      city: props.city || props.municipality,
      state: props.state || props.region,
      postcode: props.postcode,
      country: props.country,
      country_code: props.country_code,
      lat: props.lat,
      lon: props.lon,
      result_type: props.result_type,
      rank: props.rank
        ? {
            confidence: props.rank.confidence,
            match_type: props.rank.match_type,
          }
        : undefined,
    };
  }
}

// Interface for structured address data
export interface GeoApifyAddress {
  place_id: string;
  formatted: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postcode?: string;
  country?: string;
  country_code?: string;
  lat?: number;
  lon?: number;
  result_type?: string;
  rank?: {
    confidence?: number;
    match_type?: string;
  };
}

// Geoapify API response interfaces
interface GeoapifyProperties {
  place_id: string;
  osm_id?: string;
  osm_type?: string;
  country?: string;
  country_code?: string;
  region?: string;
  state?: string;
  county?: string;
  city?: string;
  municipality?: string;
  postcode?: string;
  district?: string;
  suburb?: string;
  street?: string;
  housenumber?: string;
  lon: number;
  lat: number;
  result_type: string;
  formatted: string;
  address_line1?: string;
  address_line2?: string;
  category?: string;
  timezone?: {
    name: string;
    offset_STD: string;
    offset_DST: string;
  };
  plus_code?: string;
  plus_code_short?: string;
  rank?: {
    importance: number;
    confidence: number;
    confidence_city_level: number;
    match_type: string;
  };
}

interface GeoapifyFeature {
  type: string;
  properties: GeoapifyProperties;
  geometry: {
    type: string;
    coordinates: [number, number];
  };
}

interface GeoapifyResponse {
  type: string;
  features: GeoapifyFeature[];
  query: {
    text: string;
    parsed: any;
  };
}
